import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';

import { DEFAULT_ACTION } from './types';
import type { ActionData, ActionStateValue } from './types';

const ActionStateContext = createContext<ActionStateValue | null>(null);

export const ActionStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [newAction, setNewActionState] = useState<ActionData>({ ...DEFAULT_ACTION });
  const [currentAction, setCurrentActionState] = useState<ActionData>({ ...DEFAULT_ACTION });
  const [modalNewAction, setModalNewAction] = useState(false);
  const [modalEditAction, setModalEditAction] = useState(false);
  const [stepAction, setStepAction] = useState(0);
  const [optionsAction, setOptionsAction] = useState<Record<string, any>>({});

  const openNewActionModal = useCallback(() => setModalNewAction(true), []);
  const closeNewActionModal = useCallback(() => {
    setModalNewAction(false);
    setNewActionState({ ...DEFAULT_ACTION });
    setStepAction(0);
    setOptionsAction({});
  }, []);
  const setNewAction = useCallback((data: ActionData) => setNewActionState(data), []);
  const setStepActionExplicit = useCallback((step: number) => setStepAction(step), []);
  const nextStepAction = useCallback(() => setStepAction(s => s + 1), []);
  const previousStepAction = useCallback(() => setStepAction(s => (s === 0 ? 0 : s - 1)), []);

  const openEditActionModal = useCallback(() => setModalEditAction(true), []);
  const closeEditActionModal = useCallback(() => {
    setModalEditAction(false);
    setCurrentActionState({ ...DEFAULT_ACTION });
  }, []);
  const setCurrentAction = useCallback((action: ActionData) => setCurrentActionState(action), []);

  const addOptionAction = useCallback((field: string, value: any) => {
    setOptionsAction(prev => {
      const updated = cloneDeep(prev);
      updated[field] = value;
      return updated;
    });
  }, []);
  const resetOptionsAction = useCallback(() => setOptionsAction({}), []);
  const resetOptionAction = useCallback((field: string) => {
    setOptionsAction(prev => cloneDeep(omit(prev, field)));
  }, []);

  const value = useMemo<ActionStateValue>(
    () => ({
      newAction,
      currentAction,
      modalNewAction,
      modalEditAction,
      stepAction,
      optionsAction,
      openNewActionModal,
      closeNewActionModal,
      setNewAction,
      setStepAction: setStepActionExplicit,
      nextStepAction,
      previousStepAction,
      openEditActionModal,
      closeEditActionModal,
      setCurrentAction,
      addOptionAction,
      resetOptionsAction,
      resetOptionAction
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newAction, currentAction, modalNewAction, modalEditAction, stepAction, optionsAction]
  );

  return <ActionStateContext.Provider value={value}>{children}</ActionStateContext.Provider>;
};

export function useActionState(): ActionStateValue {
  const ctx = useContext(ActionStateContext);

  if (!ctx) {
    throw new Error('useActionState must be used within a ConnectProvider');
  }

  return ctx;
}
